/**
 * FinishEarlyModalController is a class that manages the behavior of a modal for finishing a task early.
 * It handles various user interactions and updates the UI accordingly.
 */
class FinishEarlyModalController {
    /**
     * Constructor for FinishEarlyModalController.
     * @param {Object} $scope - AngularJS scope object.
     * @param {Object} $uibModalInstance - Instance of the modal.
     * @param {Object} task - The task object.
     * @param {Object} $location - AngularJS $location service.
     * @param {Object} countDown - Countdown service.
     * @param {Object} $bridges - Bridges service.
     * @param {Object} $timeout - AngularJS $timeout service.
     * @param {Object} $config - Configuration service.
     */
    constructor($scope, $uibModalInstance, task, $location, countDown, $bridges, $timeout, $config) {
        this.$scope = $scope;
        this.$uibModalInstance = $uibModalInstance;
        this.task = task;
        this.$location = $location;
        this.countDown = countDown;
        this.$bridges = $bridges;
        this.$timeout = $timeout;
        this.$config = $config;

        this._journalTime = $config.getJournalTime();
        this.$eventLog = $bridges.eventLog();

        this.$scope.hasJournal = task.hasJournal();
        this.$scope.completeJournalView = false;

        this.$scope.finishEarly = false;
        this.$scope.isValid = false;

        this.$scope.firstname = null;
        this.$scope.lastname = null;

        this.$scope.explanationMinChar = 1;
        this.$scope.disabledQuitBtn = true;

        this.$scope.onFinishEarly = this.onFinishEarly.bind(this);
        this.$scope.onCompleteJournalView = this.onCompleteJournalView.bind(this);
        this.$scope.onConfirmFinishEarly = this.onConfirmFinishEarly.bind(this);
        this.$scope.onJournalBtn = this.onJournalBtn.bind(this);
        this.$scope.onValidation = this.onValidation.bind(this);

        this.$scope.$watch('eventExplanation', this.onEventExplanationChange.bind(this));
        this.$scope.$on('$routeChangeStart', this.onRouteChangeStart.bind(this));
    }

    /**
     * Logs the event with the firstname and lastname.
     * SMELL: This method is tightly coupled with the scope and should ideally be decoupled.
     */
    _log() {
        const _message = 'Firstname:' + this.$scope.firstname + ' Lastname:' + this.$scope.lastname;
        this.$eventLog.logEvent("Finish Early Assessment by Candidate", 'finishEarlyByCandidate', _message, '', {});
    }

    /**
     * Sets the finishEarly flag to true.
     */
    onFinishEarly() {
        this.$scope.finishEarly = true;
    }

    /**
     * Sets the completeJournalView flag to true.
     */
    onCompleteJournalView() {
        this.$scope.completeJournalView = true;
    }

    /**
     * Confirms finishing the task early and handles the necessary actions.
     */
    onConfirmFinishEarly() {
        this.countDown.setTaskExpired();
        if (!this.$scope.hasJournal || this.$scope.completeJournalView) {
            this.$uibModalInstance.dismiss('cancel');

            this.countDown.resetTime(this._journalTime, this._journalTime, 'journal');

            this._log();

            this.$location.path('/taskdone/' + this.$bridges.authentication().getUserUUID());
        }
    }

    /**
     * Handles the journal button click event.
     */
    onJournalBtn() {
        this.countDown.setTaskExpired();
        this.countDown.setMode('journal');
        this.countDown.resetTime(this._journalTime, 0, 'journal');

        this._log();

        this.$timeout(() => {
            this.$scope.$apply(() => {
                this.countDown.startMeasure();
                this.$location.path('/sheet/' + this.task.getJournal().getUUID());
            });
        }, 1);
    }

    /**
     * Validates the firstname and lastname fields.
     */
    onValidation() {
        this.$scope.isValid = false;
        if (this.$scope.firstname && this.$scope.firstname.length > 0 && this.$scope.lastname && this.$scope.lastname.length > 0) {
            this.$scope.isValid = true;
        }
    }

    /**
     * Handles changes to the event explanation field.
     */
    onEventExplanationChange() {
        if (this.$scope.eventExplanation === false) {
            this.$scope.disabledQuitBtn = false;
            return false;
        }
        if (this.$scope.eventExplanation && this.$scope.eventExplanation.length >= this.$scope.explanationMinChar) {
            this.$timeout(() => {
                this.$scope.$apply(() => {
                    this.$scope.disabledQuitBtn = false;
                });
            }, 1);
        } else {
            this.$scope.disabledQuitBtn = true;
        }
    }

    /**
     * Handles the route change start event.
     * @param {Object} next - The next route.
     * @param {Object} current - The current route.
     */
    onRouteChangeStart(next, current) {
        this.$uibModalInstance.close();
    }
}

export default FinishEarlyModalController;